// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartApi.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import reportPartStyleConfigurationFactories from 'share/factories/Izenda.ReportPartStyleConfigurationFactories';
import reportPartConfigurationFactories from 'share/factories/Izenda.ReportPartConfigurationFactories';
import ReportPartContent from 'logic/models/Izenda.ReportPartContent';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import ReportPartElementUtils from 'share/utils/Izenda.ReportPartElementUtils';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';

const ReportPartUtils = { ...ReportPartElementUtils, ...HighchartUtils };

export default {
  /**
   * Get the report part type constants
   */
  get REPORT_PART_TYPES() {
    return REPORT_PART_TYPE;
  },

  /**
   * Get the chart style constants
   */
  get CHART_STYLES() {
    return CHART_TYPE;
  },

  /**
   * Get the utility functions that help to manipulate options on the custom chart options builder
   */
  get ReportPartUtils() {
    return ReportPartUtils;
  },

  /**
   * Set configuration of a report part type. It is typically using for customizing the existing report part type (i.e. Chart, Gauge or Map).
   * Instead of setting a new configuration, it recommends getting the current configuration via getReportPartConfiguration, change appropreate property,
   * then set it back using this function. By changing this configuration, it would impact to all report part styles of the particular report part type. For example,
   * adding a new field container schema to the Chart configuration will result the new field container appears in all chart type configuration designer.
   * @param {string} reportPartType Report part type
   * @param {Object} configuration Report part type configuration
   * @param {string} configuration.visualEngine The name of default visualization engine. It will be used as default visualization engine if the report part style configuration
   * doesn't specify explicitly
   * @param {Function} configuration.optionsBuilder The options builder class or constructor function to build visualization options
   * @param {Function} configuration.model The report part content model class or constructor function. This class/constructor is used to construct ReportPartContent object
   * @param {Array} configuration.fieldContainerSchema An array of field container schema
   * @param {Array} configuration.propertyWidgets An array of custom React components to create property editor.It would be useful in case of using a custom React component directly in the propertySchema property, instead of
   * using registerPropertyEditor to register a custom property editor by type.
   * @param {Object} configuration.propertySchema The object contains defined schema of property editor of report part in designer
   * @param {Function} configuration.propertyMappingProps The function to create the mapping properties object
   * @param {Function} configuration.propertyMappingSource The function to create the mapping source object
   * @param {Function} configuration.propertyValueChange The function to create value change handler
   * @param {Object} [configuration.optionsMapping] Mapping to visualization options. This settings is optional as you can write code in options builder to populate visualization object
   * @example
   * setReportPartConfiguration(REPORT_PART_TYPES.Chart, {
   *    visualEngine: 'Highcharts',
   *    // The builder class to build chart visualization options
   *    optionsBuilder: CustomHighchartOptionBuilder,
   *    // Report part content model class
   *    model: CustomReportPartChartContent,
   *    // Default field container schema
   *    fieldContainerSchema: null,
   *    // Array of custom React component to create property editor.
   *    // It would be useful in case of using a custom React component directly in the propertySchema property, instead of
   *    // using registerPropertyEditor to register a custom property editor by type.
   *    propertyWidgets: [],
   *    // The object contains defined schema of property editor of report part in designer
   *    propertySchema: {
   *      schemaKey: 'Example',
   *      groups: {
   *        example_group_name: {
   *          title: 'Example Group',
   *          fields: {
   *            example_field_name: {
   *              factory: createDropDownSchema,
   *              title: 'Title of example_field_name',
   *              value: '<any property name or getter function of propertyMappingProps, propertyMappingSource object>',
   *              props: {
   *                defaultValue: 'value1',
   *                options: ['value1', 'value2']
   *              }
   *            }
   *          }
   *        }
   *      }
   *    },
   *    // The function to create the mapping properties
   *    propertyMappingProps: function (reportPartDetails, fieldStore) {
   *      return {
   *        otherProperty: 'Value1'
   *      }
   *    },
   *    // The function to create the mapping source
   *    propertyMappingSource: function (reportPartDetails, fieldStore) {
   *      return {
   *        example_field_name: 'Value1'
   *      }
   *    },
   *    // The function to create an value changed handler
   *    propertyValueChange: function (reportPartDetails, fieldStore) {
   *      return function (schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo, derivedChange) {
   *        // It must return a Promise with chartProperties object.
   *        return Promise.resolve({ needToUpdate: false, chartProperties: reportPartProperties })
   *      }
   *    },
   *    // Mapping to visualization options.
   *    // This settings is optional as you can write code in options builder to populate visualization object
   *    optionsMapping: null
   * });
   */
  setReportPartConfiguration(reportPartType: string, configuration: Object) {
    reportPartConfigurationFactories.setConfiguration(reportPartType, configuration);
  },

  /**
   * Get configuration of a report part type
   * @param {string} reportPartType Report part type
   * @example <caption>Gets the report part configuration object of Chart</caption>
   * getReportPartConfiguration(REPORT_PART_TYPES.Chart)
   */
  getReportPartConfiguration(reportPartType: string): Object {
    return reportPartConfigurationFactories.getConfiguration(reportPartType);
  },

  /**
   * Extends report part with a new style configuration.
   * The new style appears on the Chart type list of Chart, Gauge style list of Gauge or Map type list of Map which depends on which REPORT_PART_TYPES is specified.
   * @param {string} reportPartType The report part type
   * @param {string} reportPartStyle The new report part style
   * @param {string} baseStyle A chart type that new chart derives on
   * @param {Object} configuration New chart type configuration
   * @param {string} [configuration.visualEngine] The name of visualization engine. The default visualization engine which is defined in report part configuration is used if it doesn't specify here.
   * @param {string} configuration.visualType Visual type to identify which visualization type to be rendered. For example, it would be "type" property of Highchart options.
   * @param {string} configuration.visualLabel The label text of this report part style showing in the chart type dropdown of report designer.
   * @param {Function} configuration.optionsBuilder The options builder class or constructor function to build visualization options
   * @param {Array} [configuration.fieldContainerSchema] An array of field container schema.
   * @param {Array} [configuration.propertyWidgets] An array of custom React components to create property editor.It would be useful in case of using a custom React component directly in the propertySchema property, instead of
   * using registerPropertyEditor to register a custom property editor by type.
   * @param {Object} [configuration.propertySchema] The object contains defined schema of property editor of report part in designer
   * @param {Function} [configuration.propertyMappingProps] The function to create the mapping properties object
   * @param {Function} [configuration.propertyMappingSource] The function to create the mapping source object
   * @param {Function} [configuration.propertyValueChange] The function to create value change handler
   * @param {Object} [configuration.optionsMapping] Mapping to visualization options. This settings is optional as you can write code in options builder to populate visualization object
   * @example <caption>Extend Chart with an additional 3D Column chart type</caption>
   * extendReportPartStyleConfiguration(REPORT_PART_TYPES.Chart, '3DColumn', CHART_STYLES.Column, {
   *    visualType: 'column',
   *    visualLabel: '3D Column',
   *    propertySchema: {
   *       groups: {
   *         chart: {
   *           fields: {
   *             '3d': {
   *               factory: createCheckBoxSchema,
   *               title: 'Enable 3D',
   *               value: 'getValueByKey',
   *               props: {
   *                 title: 'Enable 3D'
   *               }
   *             }
   *           }
   *         }
   *       }
   *    },
   *    propertyValueChange: (reportPartDetails, fieldStore) => (
   *       chartProperties,
   *       schemaData,
   *       changedKey,
   *       changedKeyPath,
   *       changedValue,
   *       changedOthersInfo
   *     ) => {
   *       const threeDOptions = schemaData.chart['3d'];
   *       chartProperties.optionByType['3d'] = threeDOptions ? threeDOptions.value : false;
   *    },
   *    optionsBuilder: ThreeDColumnChartOptionsBuilder,
   *    optionsMapping: {
   *       optionsByType: {
   *         '3d': { propKey: 'chart.options3d.enabled', defaultValue: true }
   *       }
   *    }
   * })
   */
  extendReportPartStyleConfiguration(
    reportPartType: string,
    reportPartStyle: string,
    baseStyle: string,
    configuration: Object
  ) {
    return reportPartStyleConfigurationFactories.extendConfiguration(
      reportPartType,
      reportPartStyle,
      baseStyle,
      configuration
    );
  }
};



// WEBPACK FOOTER //
// ./ui/api/Izenda.ReportPartApi.js